import { useCallback, useEffect, useRef, useState } from "react";
import { BsMicFill } from "react-icons/bs";
import styles from "./Breathe.module.css";
import { useMicRecorder } from "../../../hooks/use-mic-recorder";
import { useModus } from "../../../hooks/use-modus";

// Add type declaration for webkit prefix
declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

export const Breathe = ({
  size = 750,
  orbFontSizeOverride,
  onClick
}: {
  size?: number;
  orbFontSizeOverride?: string;
  onClick?: () => void;
}) => {
  const ballRef = useRef<HTMLDivElement>(null);
  const {
    volume,
    isRecording,
    formattedTime,
    isBusy,
    isInitializing,
    isProcessingAudio
  } = useMicRecorder();
  const { isWaitingForResponse } = useModus();
  const triggeredBreathing = useRef<boolean>(false);
  const isBreathing = useRef<boolean>(false);

  const [recordText, setRecordText] = useState("Record");
  const [recordColor, setRecordColor] = useState("#A27676");

  const breathe = useCallback(() => {
    if (!ballRef.current) return;

    isBreathing.current = true;
    const ball = ballRef.current;

    // Set a fast transition for the breathing effect
    ball.style.transition = "transform 0.25s ease-in-out";
    // Animate: shrink to 50% of its normal size
    ball.style.transform = "scale(0.95)";
    // After 250ms (shrink phase), expand back to normal
    setTimeout(() => {
      if (!ball) return;
      ball.style.transform = "scale(1)";
      // Wait 250ms for the expansion to complete before resuming normal scaling
      setTimeout(() => {
        if (!ball) return;
        ball.style.transition = ""; // Clear transition so dynamic scaling resumes smoothly
        isBreathing.current = false;
      }, 250);
    }, 250);
  }, []);

  const updateBallSize = useCallback(() => {
    if (!ballRef.current) return;

    // Calculate dynamic scale based on the current volume
    let scale = 1 + volume / 250; // Adjust sensitivity (tweak the divisor as needed)
    scale = Math.min(scale, 1.25); // Cap maximum scale

    // When sound is present, update the ball normally
    if (volume >= 2) {
      // Reset the breathing trigger if audio resumes
      triggeredBreathing.current = false;
      if (!isBreathing.current) {
        ballRef.current.style.transform = `scale(${scale})`;
      }
    } else {
      // When sound is absent:
      // Trigger the breathing effect only once after audio stops
      if (!triggeredBreathing.current && !isBreathing.current) {
        triggeredBreathing.current = true;
        breathe();
      }
      // Keep the ball static at its default scale when not breathing
      if (!isBreathing.current) {
        ballRef.current.style.transform = "scale(1)";
      }
    }
  }, [volume, breathe]);

  useEffect(() => {
    updateBallSize();
  }, [volume, updateBallSize]);

  useEffect(() => {
    if (isInitializing) {
      setRecordText("Initializing Mic");
      setRecordColor("#FF4343");
    } else if (isRecording || isBusy) {
      setRecordText("Stop");
      setRecordColor("#FF4343");
    } else {
      setRecordText("Talk");
      setRecordColor("#A27676");
    }
  }, [isRecording, isBusy, isInitializing]);

  return (
    <div className={styles.body} onClick={onClick}>
      <div
        ref={ballRef}
        className={styles.ball}
        style={{
          width: `${size * (275 / 750)}px`,
          height: `${size * (275 / 750)}px`
        }}
      />
      {!isWaitingForResponse && !isProcessingAudio && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
            zIndex: 1
          }}
        >
          <p
            style={{
              fontSize: orbFontSizeOverride || "12px",
              fontWeight: "500",
              margin: 0,
              color: "#A27676"
            }}
          >
            {!isInitializing && <BsMicFill size={17} color={recordColor} />}
            <br />
            {recordText}
            <br />
            {!isInitializing && formattedTime}
          </p>
        </div>
      )}
    </div>
  );
};
